import { Button, Stack } from '@mantine/core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useDeleteDataExport } from 'src/suitability-report/hooks/useDeleteDataExport';
import { useDeleteAgenda } from 'src/suitability-report/hooks/useDeleteAgenda';
import { useDeleteSuitabilityReport } from 'src/suitability-report/hooks/useDeleteSuitabilityReport';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { dateMatchesSearch } from 'src/meetings/utils/date-utils';
import { useDeleteMeetingNote } from 'src/meeting-note/hooks/useDeleteMeetingNote';
import FileUploadLayout from './FileUploadLayout';
import { useGeneratedDocuments } from './hooks/useGeneratedDocuments';
import EmptyView from './components/EmptyView';
import GenerateMenu from './components/GenerateMenu';
import GeneratedDocumentCard, {
  GeneratedDocumentType as DocumentType,
  GENERATED_DOCUMENT_TYPE_LABELS as DOCUMENT_TYPE_LABELS,
} from './components/GeneratedDocumentCard';

type DocumentToDelete = {
  id: string;
  displayName: string;
  documentType: string;
};

type SiestaAIGeneratedProps = {
  documentTypeFilter?: DocumentType;
};

export default function SiestaAIGenerated({ documentTypeFilter }: SiestaAIGeneratedProps) {
  const { documents, isLoading, error, refetch } = useGeneratedDocuments();
  const { clientId } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const {
    deleteSuitabilityReport,
    error: deleteSuitabilityReportError,
    reset: resetDeleteSuitabilityReport,
    isLoading: isDeletingSuitabilityReport,
  } = useDeleteSuitabilityReport();
  const {
    deleteMeetingAgenda,
    error: deleteAgendaError,
    reset: resetDeleteAgenda,
    isLoading: isDeletingAgenda,
  } = useDeleteAgenda();
  const {
    deleteDataExport,
    error: deleteDataExportError,
    reset: resetDeleteDataExport,
    isLoading: isDeletingDataExport,
  } = useDeleteDataExport();
  const {
    deleteMeetingNote,
    error: deleteMeetingNoteError,
    reset: resetDeleteMeetingNote,
    isLoading: isDeletingMeetingNote,
  } = useDeleteMeetingNote();

  const navigate = useNavigate();

  const openSuitabilityFlow = () =>
    navigate(`/client/${clientId}/suitability-report`, {
      state: { fromPath: location.pathname },
    });
  const openAgendaFlow = () =>
    navigate(`/client/${clientId}/meeting-agenda`, { state: { fromPath: location.pathname } });
  const openDataExportFlow = () =>
    navigate(`/client/${clientId}/data-export`, { state: { fromPath: location.pathname } });
  const openMeetingNote = () =>
    navigate(`/client/${clientId}/meeting-note`, { state: { fromPath: location.pathname } });

  const generateFlowByType: Record<DocumentType, () => void> = {
    'suitability-report': openSuitabilityFlow,
    'meeting-agenda': openAgendaFlow,
    'meeting-note': openMeetingNote,
    'data-export': openDataExportFlow,
  };

  // On a filtered sub-tab the type is known, so "Generate" produces that type
  // directly; the unfiltered view keeps the dropdown to pick a type.
  const renderGenerateControl = (buttonText: string) =>
    documentTypeFilter ? (
      <Button
        variant="primary"
        color="brand.6"
        size="md"
        onClick={generateFlowByType[documentTypeFilter]}
        data-testid="generate-button"
      >
        {buttonText}
      </Button>
    ) : (
      <GenerateMenu
        openSuitabilityFlow={openSuitabilityFlow}
        openAgendaFlow={openAgendaFlow}
        openDataExportFlow={openDataExportFlow}
        openMeetingNote={openMeetingNote}
        buttonText={buttonText}
      />
    );

  const [searchValue, setSearchValue] = useState('');
  const [documentToDelete, setDocumentToDelete] = useState<DocumentToDelete | null>(null);

  const handleDeletePress = (document: DocumentToDelete) => {
    resetDeleteSuitabilityReport();
    resetDeleteAgenda();
    resetDeleteDataExport();
    resetDeleteMeetingNote();
    setDocumentToDelete(document);
  };

  const deleteErrorByType: Record<string, { message: string } | null> = {
    'suitability-report': deleteSuitabilityReportError,
    'meeting-agenda': deleteAgendaError,
    'data-export': deleteDataExportError,
    'meeting-note': deleteMeetingNoteError,
  };
  const deleteError = documentToDelete ? deleteErrorByType[documentToDelete.documentType] : null;

  const handleDownload = async (fileId: number) => {
    try {
      const link = await createLink(fileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download document',
        'An error occurred while trying to download the document. Please try again later.',
        e,
        { clientId, fileId },
      );
      return;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      switch (documentToDelete.documentType) {
        case 'suitability-report':
          await deleteSuitabilityReport(clientId, documentToDelete.id);
          break;
        case 'meeting-agenda':
          await deleteMeetingAgenda(documentToDelete.id);
          break;
        case 'data-export':
          await deleteDataExport(clientId, documentToDelete.id);
          break;
        case 'meeting-note':
          await deleteMeetingNote(clientId, documentToDelete.id);
          break;
        default:
          throw new Error(`Unknown document type: ${documentToDelete.documentType}`);
      }
    } catch (e) {
      notifyAndLogError(
        'Failed to delete document',
        'An error occurred while trying to delete the document. Please try again later.',
        e,
        { clientId, documentId: documentToDelete.id, documentType: documentToDelete.documentType },
      );
    }
    setDocumentToDelete(null);
    await refetch();
  };

  const title = documentTypeFilter
    ? DOCUMENT_TYPE_LABELS[documentTypeFilter]
    : 'Siesta AI generated';

  const typedDocuments = documentTypeFilter
    ? documents.filter(doc => doc.documentType === documentTypeFilter)
    : documents;

  const filteredDocuments = searchValue.trim()
    ? typedDocuments.filter(doc => {
        const searchLower = searchValue.toLowerCase();
        return (
          doc.displayName.toLowerCase().includes(searchLower) ||
          DOCUMENT_TYPE_LABELS[doc.documentType].toLowerCase().includes(searchLower) ||
          dateMatchesSearch(doc.createdAt, searchLower)
        );
      })
    : typedDocuments;

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || new Error('Failed to create download link')} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (typedDocuments.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title={title}>
        <EmptyView
          title="Your AI-generated documents will appear here"
          subtitle="Generate suitability reports, meeting agendas, and data exports with Siesta AI. All your AI-created documents will be organised in one convenient location."
          customCta={renderGenerateControl('Generate new')}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title={title}
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={filteredDocuments.length}
      actions={renderGenerateControl('Generate')}
    >
      <Stack gap="xxs">
        {isLoading && filteredDocuments.length === 0 && <PageLoader absolute={false} />}
        {filteredDocuments.map(document => (
          <GeneratedDocumentCard
            key={`${document.documentType}-${document.id}`}
            displayName={document.displayName}
            documentType={document.documentType}
            createdAt={document.createdAt}
            fileId={document.fileId}
            renameFileId={document.renameFileId}
            onDownload={handleDownload}
            onDelete={() =>
              handleDeletePress({
                id: document.id,
                displayName: document.displayName,
                documentType: document.documentType,
              })
            }
          />
        ))}
      </Stack>

      <ConfirmDeleteModal
        opened={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete document"
        message={`Are you sure you want to delete "${documentToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={
          isDeletingSuitabilityReport ||
          isDeletingAgenda ||
          isDeletingDataExport ||
          isDeletingMeetingNote
        }
      />
    </FileUploadLayout>
  );
}
